import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import { DataGrid } from '@mui/x-data-grid';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import PageHeader from '../../components/common/PageHeader';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { fetchO365Licenses, fetchO365Users } from '../../features/o365/O365Slice';

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR');
};

const bytesToGb = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return null;
  return number / (1024 ** 3);
};

const getLicenseLabel = (license = {}, licenseNameBySku = {}) => {
  if (typeof license === 'string') return licenseNameBySku[license] || license;
  return license.skuLicense || license.licenseName || license.skuPartNumber || license.name || licenseNameBySku[license.skuId] || license.skuId || 'Licence';
};

function useClientOrganizationNames() {
  const { data: organizations = [] } = useSelector((state) => state.organizations.organizations);
  return useMemo(
    () => organizations.map((organization) => organization.name).filter(Boolean),
    [organizations],
  );
}

export default function ClientO365Users() {
  const dispatch = useDispatch();
  const organizationNames = useClientOrganizationNames();
  const { data: o365Users = [], loading, error, initialized } = useSelector((state) => state.o365.users);
  const { data: o365Licenses = [], loading: licensesLoading, error: licensesError, initialized: licensesInitialized } = useSelector((state) => state.o365.licenses);
  const [includeExternal, setIncludeExternal] = useState(false);
  const [licensedOnly, setLicensedOnly] = useState(true);
  const [search, setSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState({ name: '', email: '', license: '', created: '', status: '', mailbox: '' });
  const [gridFilters, setGridFilters] = useState({ name: '', email: '', license: [], created: null, status: [] });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  useEffect(() => {
    if ((!initialized || error) && !loading) dispatch(fetchO365Users());
    if ((!licensesInitialized || licensesError) && !licensesLoading) dispatch(fetchO365Licenses());
  }, [dispatch, initialized, loading, error, licensesInitialized, licensesLoading, licensesError]);

  const clientName = organizationNames[0] || 'Client';
  const users = useMemo(() => {
    const hasOrganizationName = o365Users.some((user) => user.organizationName);
    if (!organizationNames.length || !hasOrganizationName) return o365Users;
    return o365Users.filter((user) => organizationNames.includes(user.organizationName));
  }, [o365Users, organizationNames]);
  const externalUsers = useMemo(() => users.filter((user) => /#EXT#/i.test(String(user.userPrincipalName || user.mail || ''))), [users]);
  const licensedUsers = useMemo(() => users.filter((user) => Array.isArray(user.assignedLicenses) && user.assignedLicenses.length > 0), [users]);
  const visibleUsers = useMemo(() => {
    const withoutExternal = includeExternal ? users : users.filter((user) => !/#EXT#/i.test(String(user.userPrincipalName || user.mail || '')));
    return licensedOnly
      ? withoutExternal.filter((user) => Array.isArray(user.assignedLicenses) && user.assignedLicenses.length > 0)
      : withoutExternal;
  }, [users, includeExternal, licensedOnly]);

  const licenseNameBySku = useMemo(() => o365Licenses.reduce((acc, license) => {
    const label = license.licenseName || license.name || license.skuPartNumber;
    [license.skuId, license.id, license.licenseId, license.skuPartNumber, license.licenseName]
      .filter(Boolean)
      .forEach((key) => { acc[key] = label || key; });
    return acc;
  }, {}), [o365Licenses]);

  const columns = useMemo(() => [
    {
      accessorFn: (row) => row.displayName || row.userPrincipalName || 'Utilisateur',
      id: 'displayName',
      header: 'Nom',
      size: 240,
      filterVariant: 'autocomplete',
      Cell: ({ renderedCellValue }) => <Typography sx={{ fontWeight: 700 }}>{renderedCellValue}</Typography>,
    },
    {
      accessorKey: 'userPrincipalName',
      header: 'Email',
      size: 280,
    },
    {
      accessorFn: (row) => Array.isArray(row.assignedLicenses)
        ? row.assignedLicenses.map((license) => getLicenseLabel(license, licenseNameBySku)).join(' ')
        : '',
      id: 'assignedLicenses',
      header: 'Licences associées',
      size: 360,
      Cell: ({ row }) => {
        const licenses = Array.isArray(row.original.assignedLicenses) ? row.original.assignedLicenses : [];
        if (!licenses.length) return <Typography variant="body2" color="text.secondary">Aucune</Typography>;
        return (
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {licenses.map((license, index) => (
              <Chip
                key={`${getLicenseLabel(license, licenseNameBySku)}-${index}`}
                label={getLicenseLabel(license, licenseNameBySku)}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        );
      },
    },
    {
      accessorFn: (row) => formatDate(row.created),
      id: 'created',
      header: 'Créé le',
      size: 130,
    },
    {
      accessorFn: (row) => row.deleted ? 'Supprimé' : 'Actif',
      id: 'status',
      header: 'Statut',
      size: 120,
      filterVariant: 'autocomplete',
      Cell: ({ row }) => (
        <Chip size="small" color={row.original.deleted ? 'error' : 'success'} label={row.original.deleted ? 'Supprimé' : 'Actif'} />
      ),
    },
    {
      accessorFn: (row) => {
        const used = bytesToGb(row.mailboxStorageUsed);
        return used === null ? '' : used.toFixed(2);
      },
      id: 'mailboxStorageUsed',
      header: 'Mailbox',
      size: 220,
      Cell: ({ row }) => {
        const used = bytesToGb(row.original.mailboxStorageUsed);
        const limit = bytesToGb(row.original.mailboxStorageLimit);
        if (used === null || limit === null) return <Typography variant="body2" color="text.secondary">N/A</Typography>;
        const percent = Math.min((used / limit) * 100, 100);
        return (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 180 }}>
            <LinearProgress
              variant="determinate"
              value={percent}
              color={percent >= 90 ? 'error' : percent >= 75 ? 'warning' : 'primary'}
              sx={{ height: 8, borderRadius: 5, flex: 1 }}
            />
            <Typography variant="caption" color="text.secondary">{used.toFixed(1)} / {limit.toFixed(1)} Go</Typography>
          </Stack>
        );
      },
    },
  ], [licenseNameBySku]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return visibleUsers.filter((user) => {
      const licenses = (Array.isArray(user.assignedLicenses) ? user.assignedLicenses : []).map((license) => getLicenseLabel(license, licenseNameBySku)).join(' ');
      const values = {
        name: user.displayName || user.userPrincipalName || '',
        email: user.userPrincipalName || user.mail || '',
        license: licenses,
        created: formatDate(user.created),
        status: user.deleted ? 'Supprimé' : 'Actif',
        mailbox: `${user.mailboxStorageUsed || ''} ${user.mailboxStorageLimit || ''}`,
      };
      const matchesColumns = Object.entries(columnFilters).every(([key, value]) => !value || String(values[key]).toLowerCase().includes(value.toLowerCase()));
      const matchesGlobal = !query || JSON.stringify(user).toLowerCase().includes(query);
      return matchesColumns && matchesGlobal;
    });
  }, [visibleUsers, search, columnFilters, licenseNameBySku]);
  const pagedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const table = useMaterialReactTable({
    columns,
    data: visibleUsers,
    getRowId: (row) => row.id || row._id || row.userPrincipalName || row.mail || row.displayName,
    localization: MRT_Localization_FR,
    enableColumnFilterModes: true,
    enableColumnFilters: true,
    enableColumnOrdering: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    enableGlobalFilter: true,
    state: { isLoading: loading },
    initialState: { density: 'compact', showColumnFilters: true, showGlobalFilter: true, pagination: { pageSize: 50 } },
    muiSearchTextFieldProps: { size: 'small', variant: 'outlined', placeholder: 'Rechercher' },
    muiPaginationProps: { rowsPerPageOptions: [25, 50, 100], shape: 'rounded', variant: 'outlined' },
    paginationDisplayMode: 'pages',
    positionGlobalFilter: 'right',
  });
  const gridColumns = useMemo(() => [
    { field: 'displayName', headerName: 'Nom', flex: 1.1, minWidth: 220, valueGetter: (_, row) => row.displayName || row.userPrincipalName || 'Utilisateur' },
    { field: 'email', headerName: 'Email', flex: 1.2, minWidth: 260, valueGetter: (_, row) => row.userPrincipalName || row.mail || '-' },
    { field: 'licenses', headerName: 'Licences associées', flex: 1.5, minWidth: 300, sortable: false, renderCell: ({ row }) => { const values = Array.isArray(row.assignedLicenses) ? row.assignedLicenses : []; return values.length ? <Stack direction="row" flexWrap="wrap" gap={0.5}>{values.map((license, index) => <Chip key={`${index}-${getLicenseLabel(license, licenseNameBySku)}`} label={getLicenseLabel(license, licenseNameBySku)} size="small" variant="outlined" />)}</Stack> : <Typography variant="body2" color="text.secondary">Aucune</Typography>; } },
    { field: 'created', headerName: 'Créé le', minWidth: 130, valueGetter: (_, row) => formatDate(row.created) || '-' },
    { field: 'status', headerName: 'Statut', minWidth: 120, renderCell: ({ row }) => <Chip size="small" color={row.deleted ? 'error' : 'success'} label={row.deleted ? 'Supprimé' : 'Actif'} /> },
    { field: 'mailbox', headerName: 'Mailbox', flex: 1, minWidth: 220, sortable: false, renderCell: ({ row }) => { const used = bytesToGb(row.mailboxStorageUsed); const limit = bytesToGb(row.mailboxStorageLimit); if (used === null || !limit) return 'N/A'; const percent = Math.min((used / limit) * 100, 100); return <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}><LinearProgress variant="determinate" value={percent} sx={{ flex: 1, height: 7, borderRadius: 5 }} /><Typography variant="caption">{used.toFixed(1)} / {limit.toFixed(1)} Go</Typography></Stack>; } },
  ], [licenseNameBySku]);
  const filteredGridUsers = useMemo(() => visibleUsers.filter((row) => {
    const values = {
      name: row.displayName || row.userPrincipalName || '',
      email: row.userPrincipalName || row.mail || '',
      license: (Array.isArray(row.assignedLicenses) ? row.assignedLicenses : []).map((item) => getLicenseLabel(item, licenseNameBySku)).join(' '),
      created: formatDate(row.created),
      status: row.deleted ? 'Supprimé' : 'Actif',
    };
    const licenses = gridFilters.license || [];
    const statuses = gridFilters.status || [];
    return (!gridFilters.name || values.name.toLowerCase().includes(gridFilters.name.toLowerCase()))
      && (!gridFilters.email || values.email.toLowerCase().includes(gridFilters.email.toLowerCase()))
      && (!licenses.length || licenses.some((license) => values.license.toLowerCase().includes(license.toLowerCase())))
      && (!statuses.length || statuses.includes(values.status))
      && (!gridFilters.created || dayjs(row.created).isSame(dayjs(gridFilters.created), 'day'));
  }), [visibleUsers, gridFilters, licenseNameBySku]);

  return (
    <Box>
      <PageHeader section={clientName} title="Vos utilisateurs" sx={{ my: 1, mb: 3 }} />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Chip size="small" label={`${visibleUsers.length} utilisateur(s)`} />
        <Chip size="small" color="primary" variant={licensedOnly ? 'filled' : 'outlined'} label={`${licensedUsers.length} avec licence`} />
        <Button size="small" variant={licensedOnly ? 'outlined' : 'contained'} onClick={() => setLicensedOnly((value) => !value)}>
          {licensedOnly ? 'Voir tous les utilisateurs' : 'Afficher uniquement avec licence'}
        </Button>
        <Chip
          size="small"
          color={includeExternal ? 'warning' : 'default'}
          variant={includeExternal ? 'filled' : 'outlined'}
          label={includeExternal ? `Externes inclus (${externalUsers.length})` : `${externalUsers.length} externe(s) masqué(s)`}
          onClick={() => setIncludeExternal((value) => !value)}
          clickable
        />
      </Stack>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>Erreur de chargement des utilisateurs O365.</Alert> : null}
      {loading ? (
        <Skeleton variant="rectangular" width="100%" height={520} />
      ) : visibleUsers.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="h6" color="text.secondary">Aucun utilisateur O365 trouvé</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ width: '100%', bgcolor: 'background.paper' }}>
            <Box sx={{ m: { xs: 1, md: 2 }, p: { xs: 1.5, md: 2 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'action.hover' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <FilterAltOutlinedIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Filtres utilisateurs</Typography>
                <Typography variant="caption" color="text.secondary">Affinez les résultats par colonne</Typography>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField size="small" label="Nom" value={gridFilters.name} onChange={(event) => setGridFilters((current) => ({ ...current, name: event.target.value }))} sx={{ minWidth: { md: 150 }, flex: 1, bgcolor: 'background.paper' }} />
              <TextField size="small" label="Email" value={gridFilters.email} onChange={(event) => setGridFilters((current) => ({ ...current, email: event.target.value }))} sx={{ minWidth: { md: 150 }, flex: 1, bgcolor: 'background.paper' }} />
              <FormControl size="small" sx={{ minWidth: 190, flex: 1 }}><InputLabel>Licences</InputLabel><Select multiple label="Licences" value={gridFilters.license} onChange={(event) => setGridFilters((current) => ({ ...current, license: event.target.value }))} renderValue={(selected) => selected.join(', ')}>{Array.from(new Set(o365Licenses.map((item) => item.licenseName || item.name || item.skuPartNumber).filter(Boolean))).map((license) => <MenuItem key={license} value={license}>{license}</MenuItem>)}</Select></FormControl>
              <DatePicker label="Créé le" value={gridFilters.created} onChange={(value) => setGridFilters((current) => ({ ...current, created: value }))} slotProps={{ textField: { size: 'small', sx: { minWidth: 160, flex: 1, bgcolor: 'background.paper' } } }} />
              <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}><InputLabel>Statut</InputLabel><Select multiple label="Statut" value={gridFilters.status} onChange={(event) => setGridFilters((current) => ({ ...current, status: event.target.value }))} renderValue={(selected) => selected.join(', ')}><MenuItem value="Actif">Actif</MenuItem><MenuItem value="Supprimé">Supprimé</MenuItem></Select></FormControl>
              <Button variant="outlined" color="inherit" startIcon={<RestartAltIcon />} onClick={() => setGridFilters({ name: '', email: '', license: [], created: null, status: [] })} sx={{ whiteSpace: 'nowrap', minHeight: 40 }}>Réinitialiser</Button>
              </Stack>
            </Box>
            <DataGrid
              autoHeight
              rows={filteredGridUsers}
              columns={gridColumns}
              getRowId={(row) => row.id || row._id || row.userPrincipalName || row.mail || row.displayName}
              loading={loading}
              disableRowSelectionOnClick
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
              sx={{ borderRadius: 2, '& .MuiDataGrid-columnHeaders': { fontWeight: 800 }, '& .MuiDataGrid-cell': { py: 1 } }}
            />
          </Box>
          <Paper variant="outlined" sx={{ display: 'none', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <TextField size="small" value={search} onChange={(event) => { setSearch(event.target.value); setPage(0); }} placeholder="Rechercher" />
          </Box>
          <TableContainer sx={{ maxHeight: 620 }}>
            <Table stickyHeader>
              <TableHead><TableRow>
                {['Nom', 'Email', 'Licences associées', 'Créé le', 'Statut', 'Mailbox'].map((heading) => <TableCell key={heading} sx={{ fontWeight: 800 }}>{heading}</TableCell>)}
              </TableRow></TableHead>
              <TableBody>
                <TableRow>
                  {['name', 'email', 'license', 'created', 'status', 'mailbox'].map((key) => (
                    <TableCell key={key} sx={{ py: 0.75, bgcolor: 'background.paper' }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder="Filtrer..."
                        value={columnFilters[key]}
                        onChange={(event) => { setColumnFilters((current) => ({ ...current, [key]: event.target.value })); setPage(0); }}
                        inputProps={{ 'aria-label': `Filtrer ${key}` }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
              <TableBody>{pagedUsers.map((user, index) => {
                const used = bytesToGb(user.mailboxStorageUsed);
                const limit = bytesToGb(user.mailboxStorageLimit);
                const mailboxPercent = used !== null && limit ? Math.min((used / limit) * 100, 100) : 0;
                const userLicenses = Array.isArray(user.assignedLicenses) ? user.assignedLicenses : [];
                return <TableRow hover key={user.id || user._id || user.userPrincipalName || `${user.displayName}-${index}`}>
                  <TableCell sx={{ fontWeight: 700 }}>{user.displayName || user.userPrincipalName || 'Utilisateur'}</TableCell>
                  <TableCell>{user.userPrincipalName || user.mail || '-'}</TableCell>
                  <TableCell>{userLicenses.length ? userLicenses.map((license, licenseIndex) => <Chip key={`${licenseIndex}-${getLicenseLabel(license, licenseNameBySku)}`} label={getLicenseLabel(license, licenseNameBySku)} size="small" variant="outlined" sx={{ mr: .5, mb: .5 }} />) : <Typography variant="body2" color="text.secondary">Aucune</Typography>}</TableCell>
                  <TableCell>{formatDate(user.created) || '-'}</TableCell>
                  <TableCell><Chip size="small" color={user.deleted ? 'error' : 'success'} label={user.deleted ? 'Supprimé' : 'Actif'} /></TableCell>
                  <TableCell>{used === null || !limit ? 'N/A' : <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 150 }}><LinearProgress variant="determinate" value={mailboxPercent} sx={{ flex: 1, height: 7, borderRadius: 5 }} /><Typography variant="caption">{used.toFixed(1)} / {limit.toFixed(1)} Go</Typography></Stack>}</TableCell>
                </TableRow>;
              })}</TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={filteredUsers.length} page={page} onPageChange={(_, nextPage) => setPage(nextPage)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }} rowsPerPageOptions={[25, 50, 100]} labelRowsPerPage="Lignes par page" />
          </Paper>
        </>
      )}
    </Box>
  );
}
